/**
 * CallWorkDrawer — manual contact + ticket creation for the softphone.
 *
 * WHY THIS EXISTS
 * ---------------
 * Inbound calls used to hijack the workspace: the global ScreenPop rendered
 * a contact-creation form over whatever page the agent was on, and its Save
 * button never reached the API (the onSave callback only closed the panel —
 * nothing was persisted). Voice calls also auto-opened a conversation per
 * ring.
 *
 * Both are now manual. The softphone exposes "Create Contact" and "Create
 * Ticket" buttons that open this drawer — a right-side slide-over with two
 * steps:
 *
 *   contact → POST /agent/contacts (clients table, "Voice Clients" group)
 *             then an OPTIONAL "Create Ticket" next step.
 *   ticket  → POST /agent/tickets (conversation channel 'voice', carrying
 *             the call's UUID and the saved contact's client id).
 *
 * The drawer mounts at AgentLayout level (NOT inside the floating softphone
 * panel) because that panel uses scale/translate transforms — a `fixed`
 * backdrop inside it would be positioned relative to the panel instead of
 * the viewport.
 */
import { useEffect, useState, type JSX } from 'react'
import { User, Ticket, X, CheckCircle2 } from 'lucide-react'
import { api } from '../../api/client.js'
import { useToast } from '../system/ToastProvider.js'
import Select from '../ui/Select.js'

/**
 * A dialable number from a softphone caller id, or null. Portal (visitor)
 * calls present a SIP identity (`visitor_<uuid>`) instead of a number, and
 * anything over 32 chars cannot be a phone (the API caps callerPhone there).
 */
export function usablePhone(value: string | null | undefined): string | null {
  const v = value?.trim()
  if (!v) return null
  if (/^visitor[_-]/i.test(v) || v.length > 32) return null
  return v
}

export type CallWorkStep = 'contact' | 'ticket'

export interface CallWorkDrawerProps {
  open: boolean
  /** Which step to start on — 'contact' for Create Contact, 'ticket' for Create Ticket. */
  initialStep: CallWorkStep
  /** Current/last caller's E.164 number — pre-fills the phone + ticket handle. */
  callerPhone?: string | null
  /** Caller email (portal calls) — sent as the ticket's sender handle. */
  callerEmail?: string | null
  /** Call session UUID — carried into the ticket's metadata. */
  callId?: string | null
  callerName?: string | null
  /**
   * clients.id when the caller is already a saved client — the ticket links
   * it directly; the contact step is skipped as there is nothing to create.
   */
  clientId?: string | null
  /** Fired after a contact is saved so the layout can mark the caller known. */
  onContactSaved?: (client: { id: string; name: string }) => void
  onClose: () => void
}

interface CreatedContact {
  id: string
  name: string
  phoneE164: string
}

export function CallWorkDrawer({
  open,
  initialStep,
  callerPhone: rawCallerPhone,
  callerEmail,
  callId,
  callerName,
  clientId,
  onContactSaved,
  onClose,
}: CallWorkDrawerProps): JSX.Element | null {
  const callerPhone = usablePhone(rawCallerPhone)
  const toast = useToast()
  const [step, setStep] = useState<CallWorkStep>(initialStep)
  const [saving, setSaving] = useState(false)
  const [createdContact, setCreatedContact] = useState<CreatedContact | null>(null)
  const [createdTicket, setCreatedTicket] = useState<{ id: string; referenceId: string } | null>(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [groups, setGroups] = useState<Array<{ id: string; name: string; channel: string | null }>>([])
  const [groupId, setGroupId] = useState('')

  // Re-seed the form every time the drawer opens — a second caller must not
  // inherit the previous contact's fields.
  useEffect(() => {
    if (!open) return
    setStep(initialStep)
    setCreatedContact(null)
    setCreatedTicket(null)
    setName(callerName ?? '')
    setPhone(callerPhone ?? '')
    setEmail('')
    setNotes('')
    setSubject(
      callerPhone || callerName
        ? `Call from ${callerName ?? callerPhone}`
        : callerEmail
          ? `Call from ${callerEmail}`
          : '',
    )
    setDescription('')

    // Client groups for the pick — default to the tenant's voice group
    // ("Phone Banking Customers"), not the first row alphabetically.
    api
      .get<{ groups: Array<{ id: string; name: string; channel: string | null }> }>(
        '/agent/contacts/groups',
      )
      .then((res) => {
        const list = res?.groups ?? []
        setGroups(list)
        setGroupId((cur) => cur || list.find((g) => g.channel === 'voice')?.id || list[0]?.id || '')
      })
      .catch(() => setGroups([]))
  }, [open, initialStep, callerPhone, callerName, callerEmail, callId])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, saving, onClose])

  if (!open) return null

  async function saveContact(): Promise<void> {
    if (!name.trim() || !phone.trim()) return
    setSaving(true)
    try {
      const res = await api.post<{ id: string; name: string; phoneE164: string }>(
        '/agent/contacts',
        {
          name: name.trim(),
          phoneE164: phone.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          ...(groupId ? { groupId } : {}),
        },
      )
      setCreatedContact(res)
      onContactSaved?.({ id: res.id, name: res.name })
      toast.success(res && 'existing' in res ? 'Contact already saved' : 'Contact created')
      // Per the manual-workflow spec the drawer closes on save — the agent
      // raises the ticket from the softphone's New Ticket button if needed.
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create contact')
    } finally {
      setSaving(false)
    }
  }

  async function saveTicket(): Promise<void> {
    if (!subject.trim()) return
    // A contact saved this session wins; otherwise the caller's existing
    // client row (passed in by the layout's phone lookup) is linked.
    const effectiveClientId = createdContact?.id ?? clientId ?? null
    setSaving(true)
    try {
      const res = await api.post<{ ticket: { id: string; referenceId: string } }>(
        '/agent/tickets',
        {
          subject: subject.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(callId ? { callId } : {}),
          ...(effectiveClientId ? { clientId: effectiveClientId } : {}),
          ...(callerPhone ?? phone.trim() ? { callerPhone: callerPhone ?? phone.trim() } : {}),
          ...(callerEmail ? { callerEmail } : {}),
          ...(callerName || name.trim() ? { callerName: callerName ?? name.trim() } : {}),
        },
      )
      setCreatedTicket(res.ticket)
      toast.success(`Ticket ${res.ticket.referenceId} created`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create ticket')
    } finally {
      setSaving(false)
    }
  }

  const isContact = step === 'contact'

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40"
        aria-hidden="true"
        onClick={() => { if (!saving) onClose() }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={isContact ? 'Create Contact' : 'Create Ticket'}
        className="fixed right-0 top-0 z-[9999] flex h-screen w-full max-w-md flex-col bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#3366FF] text-white">
              {isContact ? <User className="h-4 w-4" /> : <Ticket className="h-4 w-4" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                {isContact ? 'Create Contact' : 'Create Ticket'}
              </h2>
              {(callerPhone || callId) && (
                <p className="text-xs text-slate-400">
                  {[callerPhone, callId ? `Call ${callId.slice(0, 8)}…` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isContact ? (
            createdContact ? (
              /* Contact saved — ticket is the optional next step. */
              <div className="flex flex-col items-start gap-3">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-semibold">
                    Contact saved — {createdContact.name}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {createdContact.phoneE164} added to Voice Clients.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep('ticket')}
                    className="rounded-sm bg-[#3366FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a45b0]"
                  >
                    Next: Create Ticket
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-sm border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Field label="Name" required>
                  <input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Contact name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone" required hint="E.164 format — a leading + and country code.">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+15551234567"
                    className={inputCls}
                  />
                </Field>
                <Field label="Client Group" hint="Defaults to the voice group (Phone Banking).">
                  <Select
                    ariaLabel="Client Group"
                    value={groupId}
                    onChange={setGroupId}
                  >
                    {groups.length === 0 && <option value="">Default group</option>}
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Notes">
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes"
                    className={inputCls}
                  />
                </Field>
              </div>
            )
          ) : createdTicket ? (
            <div className="flex flex-col items-start gap-3">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-semibold">
                  Ticket {createdTicket.referenceId} created
                </span>
              </div>
              <p className="break-all text-xs text-slate-500">ID: {createdTicket.id}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-2 rounded-sm bg-[#3366FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a45b0]"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {createdContact && (
                <p className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  Linked to contact: {createdContact.name}
                </p>
              )}
              <Field label="Subject" required>
                <input
                  autoFocus
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of the issue"
                  className={inputCls}
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Details of the request"
                  className={inputCls}
                />
              </Field>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-sm border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          {isContact && !createdContact && (
            <button
              onClick={() => void saveContact()}
              disabled={saving || !name.trim() || !phone.trim()}
              className="rounded-sm bg-[#3366FF] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#2a45b0] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {saving ? 'Saving…' : 'Save Contact'}
            </button>
          )}
          {!isContact && !createdTicket && (
            <button
              onClick={() => void saveTicket()}
              disabled={saving || !subject.trim()}
              className="rounded-sm bg-[#3366FF] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#2a45b0] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          )}
        </div>
      </aside>
    </>
  )
}

const inputCls =
  'w-full rounded-sm border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#3366FF]'

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && <small className="text-xs text-slate-500">{hint}</small>}
    </div>
  )
}
